import {
  Box,
  Divider,
  List,
  ListItem,
  ListItemText,
  SxProps,
  Theme,
} from '@mui/material'
import { DynamicContent } from 'shared/components'
import { ExtendedOption } from 'shared/types'
import { CLICKABLE_ROW_SX } from 'shared/constants/clickableRows'
import { getClickableRowProps } from 'shared/utils'
import {
  Container,
  PaginationWrapper,
  BottomButtonWrapper,
  SearchWrapper,
  ListWrapper,
} from './OptionsList.style'
import { Fragment } from 'react'

interface OptionsListProps<T, K> {
  options: ExtendedOption<T, K>[]
  loading: boolean
  PaginationComponent?: React.ReactNode
  SearchComponent?: React.ReactNode
  BottomButtonComponent?: React.ReactNode
  actionsComponent?: (option: ExtendedOption<T, K>) => React.ReactNode
  onOptionClick?: (option: ExtendedOption<T, K>) => void
  isOptionClickable?: (option: ExtendedOption<T, K>) => boolean
  listItemTextSx?: SxProps<Theme>
  listWrapperSx?: SxProps<Theme>
}

const OptionsList = <T, K>({
  options,
  loading,
  PaginationComponent,
  SearchComponent,
  BottomButtonComponent,
  actionsComponent,
  onOptionClick,
  isOptionClickable,
  listItemTextSx,
  listWrapperSx,
}: OptionsListProps<T, K>) => {
  return (
    <Container container options={options?.length > 0}>
      {SearchComponent && <SearchWrapper item>{SearchComponent}</SearchWrapper>}
      <ListWrapper item className="options-list-wrapper" sx={listWrapperSx}>
        <DynamicContent
          loading={loading && options.length === 0}
          displayContent={options.length}
          loaderSize={75}
        >
          <List disablePadding>
            {options.map(option => {
              const clickableProps = getClickableRowProps(
                option,
                onOptionClick,
                isOptionClickable
              )

              return (
                <Fragment key={`option-item-${option.value}`}>
                  <ListItem
                    key={`list-item-${option.value}`}
                    {...clickableProps}
                    sx={{
                      gap: { xs: 1, md: 0 },
                      ...(clickableProps && CLICKABLE_ROW_SX),
                    }}
                  >
                    <ListItemText
                      primary={option.title}
                      secondary={option.subtitle}
                      sx={{ minWidth: 'fit-content', ...listItemTextSx }}
                    />
                    {actionsComponent &&
                      (onOptionClick ? (
                        <Box
                          display="contents"
                          onClick={event => event.stopPropagation()}
                        >
                          {actionsComponent(option)}
                        </Box>
                      ) : (
                        actionsComponent(option)
                      ))}
                  </ListItem>
                  {options.at(-1)?.value !== option.value && <Divider />}
                </Fragment>
              )
            })}
          </List>
        </DynamicContent>
      </ListWrapper>
      {PaginationComponent && (
        <PaginationWrapper item>{PaginationComponent}</PaginationWrapper>
      )}
      {BottomButtonComponent && (
        <BottomButtonWrapper>{BottomButtonComponent}</BottomButtonWrapper>
      )}
    </Container>
  )
}

export default OptionsList
